import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Loader2, Lock, Link2, ArrowUpRight } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { useApp } from "../context/AppCtx";
import { useFetch } from "../lib/hooks";
import { api, errMsg } from "../lib/api";
import { WORLDS, worldPath } from "../lib/worlds";
import { Pill, Loading } from "./kit";
import { ago } from "../lib/fmt";

const HIDE = ["id", "name", "world", "kind", "tenant", "linked", "relationships", "activity", "location", "readiness", "milestones", "dd", "bids", "audit", "weights", "contributions", "documents", "benefits", "scenario", "qualification", "sectors", "geographies", "stages", "instruments", "products", "exceptions", "facts"];
const label = (k) => k.replace(/_/g, " ").replace(/\b(m|lpm|mw|pct|r kwh)\b/g, (x) => ({ m: "(Rm)", lpm: "(L/month)", mw: "(MW)", pct: "(%)", "r kwh": "(R/kWh)" }[x]));
const KIND_REPORT = { opportunity: "investment_factsheet", project: "project_brief", prospect: "petroleum_prospect", rfq: "procurement_summary" };

export function useGenerateReport() {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const gen = async (type, subject_id, use_ai = true) => {
    setBusy(true);
    const t = toast.loading(use_ai ? "Drafting with Claude Sonnet 4.5 + deterministic calculations…" : "Generating fact sheet…");
    try {
      const { data } = await api.post("/reports/generate", { type, subject_id, use_ai });
      toast.success(`Draft ready: ${data.title}`, { id: t });
      nav(`/studio?report=${data.id}`);
    } catch (e) { toast.error(errMsg(e), { id: t }); } finally { setBusy(false); }
  };
  return { gen, busy };
}

export function ReportButton({ type, subjectId, label = "Generate Fact Sheet", useAi = true, className = "btn btn-gold" }) {
  const { gen, busy } = useGenerateReport();
  return <button className={className} disabled={busy} onClick={() => gen(type, subjectId, useAi)} data-testid={`generate-report-${type}`}>{busy ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}{label}</button>;
}

export default function RecordDrawer() {
  const { drawer, closeRecord, openRecord } = useApp();
  const nav = useNavigate();
  const { data, loading, error } = useFetch(drawer ? `/records/${drawer}` : null, [drawer]);
  return (
    <Sheet open={!!drawer} onOpenChange={(o) => !o && closeRecord()}>
      <SheetContent className="w-full sm:max-w-[520px] overflow-y-auto scroll-thin bg-white z-[1400]" data-testid="record-drawer">
        {loading && <Loading />}
        {error && <div className="p-6 text-sm text-[#A24A2F] flex gap-2" data-testid="record-drawer-error"><Lock size={16} />{error}</div>}
        {data && !loading && (
          <>
            <SheetHeader>
              <div className="flex gap-2 flex-wrap"><Pill tone="bg-[var(--chip)] text-[var(--accent)] border-transparent">{WORLDS[data.world]?.short}</Pill><Pill tone="bg-[#F4F1EA] border-[#E5E0D5] text-[#57534E]">{data.kind}</Pill><Pill tone="bg-[#FBECE7] text-[#A24A2F] border-[#F1CEC2]">{data.confidentiality}</Pill></div>
              <SheetTitle className="font-serif text-2xl text-left" data-testid="record-drawer-title">{data.name}</SheetTitle>
              {data.location && <div className="text-[12px] text-[#8A847C] text-left">{data.location.name}, {data.location.country} · {data.location.lat.toFixed(2)}, {data.location.lng.toFixed(2)}</div>}
            </SheetHeader>
            <div className="grid grid-cols-2 gap-x-4 gap-y-3 mt-5">
              {Object.entries(data).filter(([k, v]) => !HIDE.includes(k) && v !== "" && typeof v !== "object").map(([k, v]) => (
                <div key={k}><div className="text-[10.5px] uppercase tracking-wider text-[#8A847C] capitalize">{label(k)}</div><div className="text-[13px] font-medium tnum">{typeof v === "number" ? v.toLocaleString("en-ZA") : String(v)}</div></div>
              ))}
            </div>
            <div className="mt-6">
              <div className="eyebrow !text-[10px] mb-2 flex items-center gap-1"><Link2 size={12} />Relationships & provenance</div>
              {data.relationships.length === 0 && <div className="text-[12px] text-[#8A847C]">No linked records.</div>}
              <div className="space-y-2">
                {data.relationships.map((r, i) => (
                  <button key={i} disabled={r.restricted} onClick={() => openRecord(r.id)} data-testid={`drawer-rel-${i}`} className="w-full text-left p-3 rounded-xl border border-[#EFECE4] hover:border-[#C5A059] transition-colors">
                    <div className="flex items-center gap-2"><span className="text-[13px] font-semibold flex-1">{r.restricted ? <span className="flex items-center gap-1 text-[#A24A2F]"><Lock size={12} />Restricted record</span> : r.name}</span><Pill>{r.status}</Pill></div>
                    <div className="text-[11.5px] text-[#57534E] mt-0.5">{r.label} · {WORLDS[r.world]?.short}</div>
                    <div className="text-[11px] text-[#8A847C]">Source: {r.evidence}</div>
                  </button>
                ))}
              </div>
            </div>
            {data.activity?.length > 0 && <div className="mt-6"><div className="eyebrow !text-[10px] mb-2">Activity history</div>{data.activity.map((a) => <div key={a.id} className="text-[12px] py-1.5 border-b border-[#F4F1EA]">{a.action}<span className="text-[#A8A29E]"> · {a.role} · {ago(a.at)}</span></div>)}</div>}
            <div className="flex flex-wrap gap-2 mt-6 sticky bottom-0 bg-white py-3 border-t">
              <button className="btn btn-primary" data-testid="drawer-open-in-world" onClick={() => { closeRecord(); nav(worldPath(data)); }}>Open in {WORLDS[data.world]?.short}<ArrowUpRight size={14} /></button>
              {KIND_REPORT[data.kind] && <ReportButton type={KIND_REPORT[data.kind]} subjectId={data.id} />}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
